import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase/config';
import { getAdminPermissions } from '../../api/adminService';

const SECTIONS = [
  {
    id: 'operations',
    title: '핵심 운영',
    description: '매일 쓰는 운영 도구를 모았어요.',
    items: [
      { id: 'operations', title: '업무 자동화', description: '학교·기관 케이스별로 쿠폰, 서류, 메일, 메모를 처리합니다.', path: '/admin/operations' },
      { id: 'refunds', title: '개인 결제 환불', description: '이메일이나 계정명으로 사용자를 찾아 결제 건별로 환불합니다.', path: '/admin/refunds' },
      { id: 'coupon', title: '쿠폰 관리', description: '쿠폰 발급, 검색, 회수를 처리합니다.', path: '/admin/coupon-manager' },
      { id: 'school-trial', title: '학교 무료체험 검수', description: '학교 신청을 승인하고 쿠폰을 발급합니다.', path: '/admin/school-trial' },
      { id: 'customer-leads', title: '고객 리드 현황', description: '신청, 문의, 광고 유입 리드를 한곳에서 검색합니다.', path: '/admin/leads' },
      { id: 'pricing-policy', title: '가격 안내', description: '현재 단체 할인가와 과거 이벤트 가격을 확인합니다.', path: '/admin/pricing-policy' },
      { id: 'training-guide', title: '연수 운영 가이드', description: '강사 계약부터 영상 공개, 결제, 증빙과 정산 순서를 확인합니다.', path: '/admin/training-guide' },
      { id: 'trainings', title: '연수 관리', description: '온라인 연수 소개, 차시, 영상과 공개 상태를 관리합니다.', path: '/admin/trainings' },
      { id: 'training-orders', title: '연수 주문', description: '결제, PRO 혜택, 수강권과 환불 상태를 확인합니다.', path: '/admin/training-orders' },
      { id: 'kit-shipments', title: '학급 키트 배송', description: '연간 PRO 증정·키트 예약 주문의 배송지, 발송, 환불을 관리합니다.', path: '/admin/kit' },
      { id: 'partner-settlements', title: '연수 파트너 정산', description: '강사 조건 확인, 활성화와 월별 인세 입금을 관리합니다.', path: '/admin/partner-settlements' },
    ],
  },
  {
    id: 'metrics',
    title: '분석과 매출',
    description: '성장, 매출, 광고 성과를 확인합니다.',
    items: [
      { id: 'revenue-funnel', title: '매출·퍼널 진단', description: '결제 원장과 제품 사용을 연결해 매출 병목과 개선 순서를 봅니다.', path: '/admin/revenue-funnel' },
      { id: 'ir', title: 'IR 대시보드', description: 'MRR, ARR, 결제 고객 등 투자 지표를 봅니다.', path: '/admin/ir' },
      { id: 'roas', title: 'ROAS 대시보드', description: '광고 채널별 전환과 매출을 분석합니다.', path: '/admin/roas' },
      { id: 'usage', title: '주간 사용량', description: '채팅 카테고리와 핵심 기능 사용량을 봅니다.', path: '/admin/usage' },
      { id: 'invites', title: '친구 초대', description: '공유, 가입 연결, 첫 결제와 PRO 지급을 확인합니다.', path: '/admin/invites' },
    ],
  },
  {
    id: 'rise',
    title: 'RISE 파일럿',
    description: '실증사업 운영에 필요한 민감 데이터를 관리합니다.',
    collapsible: true,
    defaultCollapsed: true,
    items: [
      { id: 'rise-dashboard', title: '실증 대시보드', description: '외부 안내용 팩트 지표와 추정 지표를 확인합니다.', path: '/pilot/rise/dashboard' },
      { id: 'rise-delivery', title: '배송 관리', description: '파일럿 키트 배송 상태를 확인합니다.', path: '/admin/pilot/delivery' },
      { id: 'rise-survey', title: '설문 응답', description: '사전, 사후 설문 응답을 확인합니다.', path: '/admin/pilot/survey' },
      { id: 'rise-coupon', title: '파일럿 쿠폰', description: 'ILOVEKNUE 쿠폰 발급 상태를 봅니다.', path: '/admin/pilot/coupon' },
      { id: 'rise-users', title: '사용자 로그', description: '파일럿 참여 교사의 사용 기록을 봅니다.', path: '/admin/pilot/users' },
      { id: 'pilot-contest', title: '공모전 관리', description: '파일럿 공모전 작품과 투표를 관리합니다.', path: '/admin/pilot-contest' },
    ],
  },
];

const styles = {
  page: {
    minHeight: '100vh',
    background: '#ffffff',
    color: '#191f28',
    fontFamily: "'Pretendard Variable', Pretendard, 'Noto Sans KR', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  },
  container: {
    width: 'min(1120px, calc(100% - 40px))',
    margin: '0 auto',
    padding: '40px 0 72px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 20,
    padding: 0,
    background: 'transparent',
    border: 0,
  },
  eyebrow: {
    margin: '0 0 8px',
    color: '#3182f6',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  title: {
    margin: 0,
    color: '#191f28',
    fontSize: 32,
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  subtitle: {
    margin: '10px 0 0',
    color: '#6b7280',
    fontSize: 15,
    lineHeight: 1.5,
  },
  summary: {
    minWidth: 120,
    padding: '16px 18px',
    borderRadius: 18,
    background: '#f8f9fa',
    textAlign: 'right',
  },
  summaryLabel: {
    display: 'block',
    marginBottom: 4,
    color: '#8b95a1',
    fontSize: 12,
    fontWeight: 600,
  },
  summaryNumber: {
    color: '#191f28',
    fontSize: 28,
    fontWeight: 800,
  },
  notice: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 32,
    padding: '14px 16px',
    borderRadius: 14,
    background: '#f8f9fa',
    color: '#6b7280',
    fontSize: 14,
  },
  noticeEmail: {
    color: '#191f28',
    fontWeight: 700,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 36,
  },
  section: {
    paddingTop: 36,
    borderTop: '1px solid #f2f4f6',
  },
  firstSection: {
    paddingTop: 4,
    borderTop: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 16,
  },
  sectionHeaderText: {
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
    color: '#191f28',
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  sectionDescription: {
    margin: '6px 0 0',
    color: '#8b95a1',
    fontSize: 14,
  },
  toggleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    padding: '0 14px',
    border: 0,
    borderRadius: 12,
    background: '#f3f4f6',
    color: '#374151',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  collapsedPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '18px 20px',
    borderRadius: 18,
    background: '#f8f9fa',
    color: '#6b7280',
    fontSize: 14,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 202,
    padding: 22,
    border: 0,
    borderRadius: 20,
    background: '#f8f9fa',
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    marginBottom: 20,
    borderRadius: 14,
    background: '#e8f3ff',
    color: '#3182f6',
    fontSize: 18,
    fontWeight: 800,
  },
  cardTitle: {
    margin: 0,
    color: '#191f28',
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  cardDescription: {
    flex: 1,
    margin: '10px 0 22px',
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.55,
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    padding: '0 18px',
    border: 0,
    borderRadius: 12,
    background: '#3182f6',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  },
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    padding: '0 18px',
    border: 0,
    borderRadius: 12,
    background: '#f3f4f6',
    color: '#374151',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  },
  panel: {
    width: 'min(420px, calc(100% - 40px))',
    margin: '120px auto 0',
    padding: '32px 24px',
    borderRadius: 20,
    background: '#f8f9fa',
    textAlign: 'center',
  },
  panelTitle: {
    margin: '0 0 10px',
    color: '#191f28',
    fontSize: 22,
    fontWeight: 800,
  },
  panelText: {
    margin: '0 0 20px',
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.5,
  },
};

const AdminHubPage = () => {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState(() => (
    SECTIONS.reduce((acc, section) => {
      if (section.defaultCollapsed) acc[section.id] = true;
      return acc;
    }, {})
  ));

  const visibleToolCount = SECTIONS.reduce((count, section) => (
    collapsedSections[section.id] ? count : count + section.items.length
  ), 0);

  const openInNewTab = (path) => {
    const url = new URL(path, window.location.origin);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      if (authLoading) return;
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const adminPerms = await getAdminPermissions();

        if (cancelled) return;
        setPermissions(adminPerms);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || '권한을 확인하지 못했어요');
        setPermissions(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user]);

  if (authLoading || isLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <p style={{ ...styles.panelText, marginBottom: 0 }}>권한을 확인하는 중</p>
        </div>
      </div>
    );
  }

  if (!user || error || !permissions?.is_admin) {
    return (
      <div style={styles.page}>
        <Helmet>
          <title>접근 권한 없음 | 왓퀴즈 Admin</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div style={styles.panel}>
          <h1 style={styles.panelTitle}>접근 권한이 없어요</h1>
          <p style={styles.panelText}>{error || '관리자 권한이 있는 계정으로 로그인해주세요.'}</p>
          <button type="button" onClick={() => navigate('/')} style={styles.secondaryButton}>
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Helmet>
        <title>관리자 홈 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.container}>
        <div style={styles.header} role="banner">
          <div>
            <p style={styles.eyebrow}>WhatQuiz Admin</p>
            <h1 style={styles.title}>관리자 홈</h1>
            <p style={styles.subtitle}>흩어진 내부 도구를 한곳에 모았어요.</p>
          </div>
          <div style={styles.summary} aria-label="관리자 도구 수">
            <span style={styles.summaryLabel}>열린 도구</span>
            <strong style={styles.summaryNumber}>{visibleToolCount}</strong>
          </div>
        </div>

        <div style={styles.notice}>
          <strong style={styles.noticeEmail}>{permissions.email || user.email}</strong>
          <span>운영 도구를 바로 열 수 있어요.</span>
        </div>

        <main style={styles.sections}>
          {SECTIONS.map((section, sectionIndex) => (
            <section
              key={section.id}
              style={sectionIndex === 0 ? { ...styles.section, ...styles.firstSection } : styles.section}
            >
              <div style={styles.sectionHeader}>
                <div style={styles.sectionHeaderText}>
                  <h2 style={styles.sectionTitle}>{section.title}</h2>
                  <p style={styles.sectionDescription}>{section.description}</p>
                </div>
                {section.collapsible && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    style={styles.toggleButton}
                    aria-expanded={!collapsedSections[section.id]}
                    aria-controls={`${section.id}-tools`}
                  >
                    {collapsedSections[section.id] ? '열기' : '닫기'}
                  </button>
                )}
              </div>

              {collapsedSections[section.id] ? (
                <div id={`${section.id}-tools`} style={styles.collapsedPanel}>
                  <span>민감한 파일럿 도구 {section.items.length}개를 접어두었어요.</span>
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    style={styles.secondaryButton}
                  >
                    펼치기
                  </button>
                </div>
              ) : (
                <div id={`${section.id}-tools`} style={styles.grid}>
                  {section.items.map((item) => (
                    <article key={item.id} style={styles.card}>
                      <span style={styles.icon} aria-hidden="true">{item.title.slice(0, 1)}</span>
                      <h3 style={styles.cardTitle}>{item.title}</h3>
                      <p style={styles.cardDescription}>{item.description}</p>
                      <button
                        type="button"
                        style={styles.button}
                        onClick={() => openInNewTab(item.path)}
                      >
                        열기
                      </button>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default AdminHubPage;
